import React, { useEffect, useState } from 'react';
import { commercialTemplates } from './commercialTemplates';
import './commercial.css';

const fallbackTimezones = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
];

const fallbackLanguages = [
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'es-US', label: 'Spanish (United States)' },
];

export default function CommercialRegistrationFields({ api, form, onFormChange }) {
  const [options, setOptions] = useState({
    templates: commercialTemplates,
    timezones: fallbackTimezones,
    languages: fallbackLanguages,
    defaultTimezone: 'UTC',
    defaultLocale: 'en-US',
  });

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      if (!api) return;

      try {
        const res = await api.get('/commercial/setup/options');
        if (!isMounted) return;

        setOptions(current => ({
          ...current,
          ...res.data,
        }));

        onFormChange(current => ({
          ...current,
          templateType: current.templateType || res.data.defaultTemplateType || 'asset_maintenance',
          timezone: current.timezone || res.data.defaultTimezone || 'UTC',
          locale: current.locale || res.data.defaultLocale || 'en-US',
        }));
      } catch (err) {
        // Fallback values are already in place, so we stay quiet here.
      }
    };

    loadOptions();
    return () => {
      isMounted = false;
    };
  }, [api]);

  const selectedTemplate =
    options.templates.find(template => template.key === form.templateType) || options.templates[0];

  const updateField = (key, value) => {
    onFormChange(current => ({
      ...current,
      [key]: value,
    }));
  };

  return (
    <div className="commercial-auth-card">
      <div className="commercial-mode-toggle" role="tablist" aria-label="Commercial registration path">
        <button
          type="button"
          className={form.commercialRegistrationMode === 'create' ? 'is-active' : ''}
          onClick={() =>
            onFormChange(current => ({
              ...current,
              commercialRegistrationMode: 'create',
              inviteCode: '',
            }))
          }
        >
          Create Company Account
        </button>
        <button
          type="button"
          className={form.commercialRegistrationMode === 'join' ? 'is-active' : ''}
          onClick={() =>
            onFormChange(current => ({
              ...current,
              commercialRegistrationMode: 'join',
              companyDomain: '',
              restrictSignupToCompanyDomain: false,
            }))
          }
        >
          Join Existing Company
        </button>
      </div>

      {form.commercialRegistrationMode === 'create' ? (
        <div className="commercial-auth-grid">
          <label className="commercial-form__field">
            <span>Company Name</span>
            <input
              type="text"
              value={form.companyName}
              onChange={event => updateField('companyName', event.target.value)}
              placeholder="Example: Mission Focused Ethos, LLC"
            />
          </label>

          <label className="commercial-form__field">
            <span>Company Domain</span>
            <input
              type="text"
              value={form.companyDomain}
              onChange={event => updateField('companyDomain', event.target.value)}
              placeholder="example.com"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <small className="commercial-form__hint">
              If you claim a company domain here, your verified registration email must match it.
            </small>
          </label>

          {form.companyDomain.trim() ? (
            <label className="commercial-toggle">
              <input
                type="checkbox"
                checked={form.restrictSignupToCompanyDomain}
                onChange={event => updateField('restrictSignupToCompanyDomain', event.target.checked)}
              />
              <span className="commercial-toggle__track" aria-hidden="true">
                <span className="commercial-toggle__thumb" />
              </span>
              <span className="commercial-toggle__copy">
                Allow future self-signups only from matching company emails for this company.
              </span>
            </label>
          ) : null}

          <label className="commercial-form__field">
            <span>Template Type</span>
            <select value={form.templateType} onChange={event => updateField('templateType', event.target.value)}>
              {options.templates.map(template => (
                <option key={template.key} value={template.key}>
                  {template.label}
                </option>
              ))}
            </select>
          </label>

          <div className="commercial-form__grid">
            <label className="commercial-form__field">
              <span>Timezone</span>
              <select value={form.timezone} onChange={event => updateField('timezone', event.target.value)}>
                {options.timezones.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="commercial-form__field">
              <span>Language</span>
              <select value={form.locale} onChange={event => updateField('locale', event.target.value)}>
                {options.languages.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {selectedTemplate ? (
            <div className="commercial-template-preview">
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>{selectedTemplate.label}</h3>
              <p style={{ marginTop: 0, color: '#4d657d' }}>{selectedTemplate.description}</p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="commercial-auth-grid">
          <label className="commercial-form__field">
            <span>Company Name</span>
            <input
              type="text"
              value={form.companyName}
              onChange={event => updateField('companyName', event.target.value)}
              placeholder="Example: Mission Focused Ethos, LLC"
            />
            <small className="commercial-form__hint">
              Use the company name if this company allows sign-up from matching work emails.
            </small>
          </label>

          <label className="commercial-form__field">
            <span>Invite Code</span>
            <input
              type="text"
              value={form.inviteCode}
              onChange={event => updateField('inviteCode', event.target.value.toUpperCase())}
              placeholder="AB12CD34"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />
            <small className="commercial-form__hint">
              Invite codes are the safer default when an existing company wants to onboard teammates.
            </small>
          </label>
        </div>
      )}
    </div>
  );
}
